import React, { useState, useEffect } from "react";
import { Card, Col, Row, Button, Form, theme, message, Segmented } from "antd";
import { useLocation } from "react-router-dom";
import { UploadOutlined } from "@ant-design/icons";
import ExistingExercise from "../ExerciseComponents/ExistingExercise";
import PrescribedExercise from "../ExerciseComponents/PrescribedExercise";
import UploadExerciseModal from "./UploadExerciseModal";
import ExistingExerciseModal from "./ExistingExerciseModal";
import { useVideoUpload } from "./useVideoUpload";
import { useExerciseData } from "./useExerciseData";
import { getAdmissionApi } from "../../../../../../services/op.service";
import "../ExerciseComponents/exerciseMobile.css";
import dayjs from "dayjs";


const { useToken } = theme;

const OpExerciseForm = ({ formData = {}, onValuesChange }) => {
  const { token } = useToken();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const admissionIdFromURL = searchParams.get("aid");

  const [admissionId] = useState(
    formData?.admission_id || admissionIdFromURL || null
  );
  const [opId, setOpId] = useState(null);
  const [form] = Form.useForm();
  const [existingForm] = Form.useForm();
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [existingModalVisible, setExistingModalVisible] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileTab, setMobileTab] = useState("prescribed");
  const {
    uploadValues,
    setUploadValues,
    uploadProps,
    dummyRequest,
    resetUploadValues,
  } = useVideoUpload(opId);

  const {
    prescribedData,
    setPrescribedData,
    addExercise,
    isDuplicateExerciseName,
  } = useExerciseData(admissionId, onValuesChange);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!admissionId) return;
    getAdmissionApi(admissionId)
      .then((res) => setOpId(res?.data?.op_id || null))
      .catch(() => {});
  }, [admissionId]);

  useEffect(() => {
    if (selectedExercise) {
      existingForm.setFieldsValue({
        name: selectedExercise.name,
        type: selectedExercise.type || selectedExercise.exercise_type,
        frequency:
          selectedExercise.frequency !== "" ? selectedExercise.frequency : null,
        intensity:
          selectedExercise.intensity !== "" ? selectedExercise.intensity : null,
        startDate: selectedExercise.startDate
        ? dayjs(selectedExercise.startDate)
        : dayjs(),
        duration: selectedExercise.duration,
      });
    } else {
      existingForm.resetFields();
    }
  }, [selectedExercise]);

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return "";
    if (url.includes("watch?v=")) {
      return url.replace("watch?v=", "embed/");
    } else if (url.includes("youtu.be/")) {
      return url.replace("youtu.be/", "www.youtube.com/embed/");
    } else if (url.includes("youtube.com/shorts/")) {
      const videoId = url.split("/shorts/")[1].split(/[?&]/)[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return url;
  };

  const resetUploadForm = () => {
    form.resetFields();
    resetUploadValues();
  };

  const handleUploadSubmit = async (closeAfter = true) => {
    if (!admissionId) {
      message.error("Admission ID is required from URL!");
      return;
    }

    try {
      const values = await form.validateFields();

      if (isDuplicateExerciseName(values.name)) {
        message.error(
          "This exercise name already exists. Please choose another name."
        );
        return;
      }

      const isLink = uploadValues.isLink;
      const payload = {
        admission: admissionId,
        name: values.name,
        exercise_type: values.type,
        frequency: Number(values.frequency),
        intensity: Number(values.intensity),
        start_date: values.startDate
        ? values.startDate.format("YYYY-MM-DD")
        : null,

        duration: parseInt(values.duration),
        time_slot: uploadValues.timeSlot || "12:00",
        source_type: isLink ? "other_source" : "file",
        others_link: isLink ? uploadValues.videoPreviewURL : null,
        file:
          !isLink && uploadValues.s3Path
            ? { path: uploadValues.s3Path.path, type: uploadValues.s3Path.type }
            : {},
      };

      const success = await addExercise(payload);
      if (success) {
        if (closeAfter) setUploadModalVisible(false);
        form.resetFields();
        resetUploadValues();
      }
    } catch (err) {
      message.error("Failed to add exercise");
    }
  };

  const handleExistingExerciseSubmit = async () => {
    if (!admissionId) {
      message.error("Admission ID is required from URL!");
      return;
    }

    try {
      const values = await existingForm.validateFields();

      if (isDuplicateExerciseName(values.name)) {
        message.error(
          "This exercise name already exists. Please choose another name."
        );
        return;
      }

      const payload = {
        admission: admissionId,
        name: values.name,
        exercise_type: values.type,
        frequency: Number(values.frequency),
        intensity: Number(values.intensity),
       start_date: values.startDate
      ? values.startDate.format("YYYY-MM-DD")
      : null,

        duration: parseInt(values.duration),
        time_slot: selectedExercise?.timeSlot || "12:00",
        exercise: selectedExercise?.id || null,
        file: selectedExercise?.s3_path?.path
          ? {
              path: selectedExercise.s3_path.path,
              type: selectedExercise.s3_path.type,
            }
          : selectedExercise?.file?.path
          ? {
              path: selectedExercise.file.path,
              type: selectedExercise.file.type,
            }
          : {},
        others_link: selectedExercise?.others_link || null,
      };

      const success = await addExercise(payload);
      if (success) {
        setExistingModalVisible(false);
        setSelectedExercise(null);
      }
    } catch (err) {
      message.error("Failed to add exercise");
    }
  };

  return (
    <div className="ip-forms">
     {isMobile && (
       <div className="exercise-mobile-toggle">
         <Segmented
           options={[
             { label: "Exercises", value: "existing" },
             { label: "Prescribed", value: "prescribed" },
           ]}
           value={mobileTab}
           onChange={(val) => setMobileTab(val)}
           block
         />
       </div>
     )}
     {isMobile ? (
           <>
           {mobileTab === "prescribed" && (
         <PrescribedExercise
           prescribedData={Array.isArray(prescribedData) ? prescribedData : []}
           setPrescribedData={setPrescribedData}
         />
     )}

    {mobileTab === "existing" && (
       <>
         <div className="mobile-upload-btn">
           <Button
             type="primary"
             icon={<UploadOutlined />}
             onClick={() => setUploadModalVisible(true)}
           >
             Upload
           </Button>
         </div>
     
         <ExistingExercise
           setSelectedExercise={setSelectedExercise}
           setExistingModalVisible={setExistingModalVisible}
           mobile
         />
       </>
     )}

  </>
) : (
  <Row gutter={16}>
    <Col xs={24} sm={24} md={16}>
      <ExistingExercise
        setSelectedExercise={setSelectedExercise}
        setExistingModalVisible={setExistingModalVisible}
      />
    </Col>

    <Col xs={24} sm={24} md={8}>
      <Card
        title="Prescribed Exercise List"
        style={{ border: "1px solid #F2F4F7" }}
        extra={
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => setUploadModalVisible(true)}
          >
            Upload
          </Button>
        }
      >
        <PrescribedExercise
          prescribedData={Array.isArray(prescribedData) ? prescribedData : []}
          setPrescribedData={setPrescribedData}
        />
      </Card>
    </Col>
  </Row>
)}


      <UploadExerciseModal
        visible={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          resetUploadForm();
        }}
        onSubmit={handleUploadSubmit}
        form={form}
        uploadValues={uploadValues}
        setUploadValues={setUploadValues}
        uploadProps={uploadProps}
        dummyRequest={dummyRequest}
        token={token}
        getYouTubeEmbedUrl={getYouTubeEmbedUrl}
        isMobile={isMobile}
      />

      <ExistingExerciseModal
        visible={existingModalVisible}
        onCancel={() => {
          setExistingModalVisible(false);
          setSelectedExercise(null);
        }}
        onSubmit={handleExistingExerciseSubmit}
        form={existingForm}
        selectedExercise={selectedExercise}
        getYouTubeEmbedUrl={getYouTubeEmbedUrl}
        isMobile={isMobile}
      />
    </div>
  );
};

export default OpExerciseForm;
